// ===========================================
// Application Constants
// ===========================================

// API
export const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

// Cognito
export const COGNITO_CONFIG = {
  region: import.meta.env.VITE_COGNITO_REGION || 'eu-west-1',
  userPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID || '',
  clientId: import.meta.env.VITE_COGNITO_CLIENT_ID || '',
};

// Maps
export const MAP_CONFIG = {
  defaultCenter: { lat: -33.9249, lng: 18.4241 }, // Cape Town
  defaultZoom: 10,
  tileUrl: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
  attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
};

// Amenity labels
export const AMENITY_LABELS: Record<string, string> = {
  tastings: 'Wine Tastings',
  restaurant: 'Restaurant',
  accommodation: 'Accommodation',
  tours: 'Farm Tours',
  picnics: 'Picnics',
  weddings: 'Weddings & Events',
  'cellar-tours': 'Cellar Tours',
  'kids-friendly': 'Kids Friendly',
  'pet-friendly': 'Pet Friendly',
  'wheelchair-accessible': 'Wheelchair Accessible',
};

// Wine type labels
export const WINE_TYPE_LABELS: Record<string, string> = {
  red: 'Red Wine',
  white: 'White Wine',
  rosé: 'Rosé',
  mcc: 'MCC (Cap Classique)',
  dessert: 'Dessert Wine',
  fortified: 'Fortified Wine',
  natural: 'Natural Wine',
  organic: 'Organic Wine',
};

// Price range labels
export const PRICE_RANGE_LABELS: Record<string, string> = {
  free: 'Free',
  budget: 'Budget (Under R100)',
  moderate: 'Moderate (R100-R250)',
  premium: 'Premium (R250+)',
};

// Subscription tier labels
export const TIER_LABELS: Record<string, string> = {
  free: 'Free',
  pro: 'Pro',
  premium: 'Premium',
};

// Subscription tier limits
export const TIER_LIMITS = {
  free: {
    photos: 3,
    descriptionLength: 500,
    features: ['basic-listing', 'contact-info', 'opening-hours'],
  },
  pro: {
    photos: 10,
    descriptionLength: 2000,
    features: ['basic-listing', 'contact-info', 'opening-hours', 'grape-varieties', 'priority-search', 'basic-analytics', 'image-optimization'],
  },
  premium: {
    photos: Infinity,
    descriptionLength: Infinity,
    features: ['basic-listing', 'contact-info', 'opening-hours', 'grape-varieties', 'priority-search', 'advanced-analytics', 'image-optimization', 'featured-listing', 'promotional-badge'],
  },
};

// Days of week
export const DAYS_OF_WEEK = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
] as const;

// Day labels
export const DAY_LABELS: Record<string, string> = {
  monday: 'Monday',
  tuesday: 'Tuesday',
  wednesday: 'Wednesday',
  thursday: 'Thursday',
  friday: 'Friday',
  saturday: 'Saturday',
  sunday: 'Sunday',
};

// Pagination defaults
export const PAGINATION = {
  defaultLimit: 12,
  maxLimit: 50,
};

// Image constraints
export const IMAGE_CONSTRAINTS = {
  maxSize: 5 * 1024 * 1024, // 5MB
  allowedTypes: ['image/jpeg', 'image/png', 'image/webp'],
  dimensions: {
    thumb: 200,
    medium: 600,
    large: 1200,
  },
};

// Routes
export const ROUTES = {
  home: '/',
  farms: '/farms',
  farmDetail: (id: string) => `/farms/${id}`,
  regions: '/regions',
  regionDetail: (id: string) => `/regions/${id}`,
  map: '/map',
  routePlanner: '/routes',
  login: '/login',
  signup: '/signup',
  verifyEmail: '/verify-email',
  forgotPassword: '/forgot-password',
  resetPassword: '/reset-password',
  completeProfile: '/complete-profile',
  profile: '/profile',
  myLists: '/my-lists',
  ownerDashboard: '/owner',
  adminDashboard: '/admin',
};

// Wine types for user preferences
export const WINE_TYPES = [
  'Red',
  'White',
  'Rosé',
  'MCC/Sparkling',
  'Dessert',
  'Fortified',
] as const;

// Common grape varieties in South Africa
export const GRAPE_VARIETIES = [
  'Cabernet Sauvignon',
  'Merlot',
  'Shiraz',
  'Pinotage',
  'Pinot Noir',
  'Cabernet Franc',
  'Chardonnay',
  'Sauvignon Blanc',
  'Chenin Blanc',
  'Viognier',
  'Semillon',
  'Riesling',
  'Gewürztraminer',
  'Colombard',
  'Muscat',
  'Grenache',
  'Mourvèdre',
  'Cinsault',
  'Tempranillo',
  'Petit Verdot',
] as const;
